import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  Grid,
  Card,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { adminMenuItems } from '../../../../../config/layout';
import { createDesignation, updateDesignation } from '../../../../../api/designationApi';
import { useErrorModal } from '../../../../../hooks/useErrorModal';
import PermissionGuard from '../../../../../components/PermissionGuard';

const AddDesignation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const locationState = useLocation();
  const isEdit = !!id;

  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = orgManagementItem?.subItems?.find(
      (item) => item.id === 'org-structure'
    );
    return currentnavItem?.breadcrumbs || [];
  }, [orgManagementItem]);

  const [formData, setFormData] = useState({
    designationName: '',
    designationCode: '',
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { showError, ErrorModal } = useErrorModal();

  useEffect(() => {
    if (isEdit) {
      const designationData = locationState.state?.designationData;
      if (designationData) {
        setFormData({
          designationName: designationData.name || '',
          designationCode: designationData.code || '',
          description: designationData.description || '',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'No designation data provided. Redirecting...',
          severity: 'error'
        });
        setTimeout(() => navigate('/org-management/org-structure/designation'), 2000);
      }
    }
  }, [isEdit, locationState.state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.designationName.trim()) {
      newErrors.designationName = 'Designation name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const designationData = {
        name: formData.designationName,
        code: formData.designationCode,
        description: formData.description,
      };

      let result;
      if (isEdit) {
        result = await updateDesignation(id, designationData);
      } else {
        result = await createDesignation(designationData);
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Designation ${isEdit ? 'updated' : 'created'} successfully!`,
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/org-management/org-structure');
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: result.message || `Failed to ${isEdit ? 'update' : 'create'} designation`,
          severity: 'error',
        });
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/org-management/org-structure');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          marginTop: 2,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb) => (
          <Link
            key={crumb.path}
            onClick={() => navigate(crumb.path)}
            sx={{
              cursor: 'pointer',
              color: '#7522D1',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {crumb.label}
          </Link>
        ))}
        <Typography sx={{ color: '#030007', fontSize: '0.875rem' }}>
          {isEdit ? 'Edit Designation' : 'Add Designation'}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ marginBottom: 4 }}>
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            padding: '20px 24px',
            marginBottom: 3,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#030007',
                marginBottom: 0.5,
                fontSize: '1.1rem',
              }}
            >
              {isEdit ? 'Edit Designation' : 'Add Designation'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                fontSize: '0.9rem',
              }}
            >
              {isEdit ? 'Update your designation information' : 'Create a new designation for your organization'}
            </Typography>
          </Box>
        </Box>

        <Card
          sx={{
            padding: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: 1,
          }}
        >
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 1,
                fontSize: '1rem',
              }}
            >
              Designation Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                marginBottom: 3,
                fontSize: '0.9rem',
              }}
            >
              {isEdit ? 'Update' : 'Enter'} the designation details and information as needed.
            </Typography>

            <Grid container spacing={3} sx={{ marginBottom: 3 }}>
              <Grid item xs={12} md={4} size={{ xs: 12, sm: 12, md: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 1,
                      backgroundColor: '#7522D1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WorkOutlineIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#030007',
                      fontSize: '1rem',
                    }}
                  >
                    Designation
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#4F4937',
                    fontSize: '0.9rem',
                  }}
                >
                  {isEdit ? 'Update' : 'Add'} the designation name, code, and description.
                </Typography>
              </Grid>

              <Grid item xs={12} md={8} size={{ xs: 12, sm: 12, md: 8 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} size={{ xs: 12, sm: 12, md: 12 }}>
                    <TextField
                      fullWidth
                      label="Designation Name *"
                      name="designationName"
                      value={formData.designationName}
                      onChange={handleChange}
                      placeholder="Enter designation name"
                      variant="outlined"
                      error={!!errors.designationName}
                      helperText={errors.designationName}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} size={{ xs: 12, sm: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Designation Code"
                      name="designationCode"
                      value={formData.designationCode}
                      onChange={handleChange}
                      placeholder="Enter designation code"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} size={{ xs: 12, sm: 12, md: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Enter description"
                      variant="outlined"
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 4,
              paddingTop: 3,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                borderColor: '#030007',
                color: '#030007',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.2,
                borderRadius: 1,
                '&:hover': {
                  borderColor: '#030007',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Cancel
            </Button>
            <PermissionGuard module="designation" action={isEdit ? "edit" : "create"}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  backgroundColor: '#7522D1',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1.2,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#5E1BA8',
                  },
                }}
              >
                {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Create Designation')}
              </Button>
            </PermissionGuard>
          </Box>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ErrorModal />
    </Container>
  );
};

export default AddDesignation;
